<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-[1650] overflow-hidden"
      @close="handleClose"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-250"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/60 backdrop-blur-[1px]" />
      </TransitionChild>

      <div class="app-scroll fixed inset-0 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
        <div class="flex min-h-full items-start justify-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-250"
            enter-from="opacity-0 translate-y-2 sm:translate-y-4 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-2 sm:translate-y-4 sm:scale-95"
          >
            <DialogPanel class="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div class="border-b border-zinc-200 px-5 py-4 sm:px-6">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <DialogTitle class="text-xl font-semibold text-zinc-900">
                      {{ isMemberMode ? 'Create Member' : 'Create Lead' }}
                    </DialogTitle>
                    <p class="mt-1 text-sm text-zinc-500">
                      {{ isMemberMode
                        ? 'Bring in an existing subscriber and assign access in one flow.'
                        : 'Add a lead record for a contact who has not completed signup yet.' }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700"
                    @click="handleClose"
                  >
                    <XMarkIcon class="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div class="app-scroll max-h-[70vh] overflow-y-auto px-5 py-5 pb-10 sm:px-6">
                <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <section class="rounded-xl bg-zinc-50 p-4">
                    <h4 class="text-sm font-semibold uppercase tracking-wide text-zinc-600">
                      Profile
                    </h4>
                    <div class="mt-4 flex items-start gap-4">
                      <div class="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-zinc-200">
                        <img
                          v-if="form.profilePicUrl"
                          :src="form.profilePicUrl"
                          class="h-full w-full object-cover"
                          alt="Avatar preview"
                        >
                        <div
                          v-else
                          class="flex h-full w-full items-center justify-center text-2xl font-semibold text-zinc-500"
                        >
                          {{ avatarInitials }}
                        </div>
                      </div>
                      <div class="space-y-2">
                        <input
                          ref="avatarInputRef"
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          class="hidden"
                          @change="handleAvatarSelected"
                        >
                        <button
                          type="button"
                          class="inline-flex items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                          :disabled="isUploadingAvatar"
                          @click="avatarInputRef?.click()"
                        >
                          <PhotoIcon class="h-5 w-5 text-zinc-500" />
                          {{ isUploadingAvatar ? 'Uploading...' : 'Upload Image' }}
                        </button>
                        <p class="text-xs text-zinc-500">
                          PNG, JPG, WEBP. Recommended square image.
                        </p>
                      </div>
                    </div>

                    <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">First name</label>
                        <input
                          v-model.trim="form.firstName"
                          type="text"
                          class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                        >
                      </div>
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Last name</label>
                        <input
                          v-model.trim="form.lastName"
                          type="text"
                          class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                        >
                      </div>
                    </div>

                    <div class="mt-3">
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Email</label>
                      <input
                        v-model.trim="form.email"
                        type="email"
                        class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                      >
                    </div>

                    <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Password</label>
                        <input
                          v-model="form.password"
                          type="password"
                          autocomplete="new-password"
                          :placeholder="isMemberMode ? 'Required' : 'Optional'"
                          class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                        >
                      </div>
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Phone (optional)</label>
                        <input
                          v-model.trim="form.phone"
                          type="text"
                          class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                        >
                      </div>
                    </div>

                    <div class="mt-3">
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Alias (optional)</label>
                      <input
                        v-model.trim="form.alias"
                        type="text"
                        class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                        placeholder="@member"
                      >
                    </div>
                  </section>

                  <section class="rounded-xl bg-zinc-50 p-4">
                    <h4 class="text-sm font-semibold uppercase tracking-wide text-zinc-600">
                      Plan & Access
                    </h4>
                    <div class="mt-3">
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan mode</label>
                      <PlatformaSelect
                        v-model="form.planMode"
                        class="mt-1"
                        :options="planModeOptions"
                      />
                    </div>

                    <div
                      v-if="form.planMode === 'existing'"
                      class="mt-3"
                    >
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan tier</label>
                      <PlatformaSelect
                        v-model="form.planTierId"
                        class="mt-1"
                        :options="planTierOptions"
                        placeholder="Select tier"
                      />
                    </div>

                    <div
                      v-if="form.planMode === 'custom'"
                      class="mt-3 space-y-3 rounded-lg border border-zinc-200 bg-white p-3"
                    >
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Custom tier name</label>
                        <input
                          v-model.trim="form.customPlanName"
                          type="text"
                          class="mt-1 h-10 w-full rounded-md border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-0"
                        >
                      </div>
                      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Billing cadence</label>
                          <PlatformaSelect
                            v-model="form.customBillingCadence"
                            class="mt-1"
                            :options="billingCadenceOptions"
                          />
                        </div>
                        <div>
                          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Currency</label>
                          <input
                            v-model.trim="form.customCurrencyCode"
                            type="text"
                            maxlength="3"
                            class="mt-1 h-10 w-full rounded-md border border-zinc-300 px-3 text-sm uppercase focus:outline-none focus:ring-0"
                          >
                        </div>
                      </div>
                      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Price</label>
                          <input
                            v-model.trim="form.customPrice"
                            type="number"
                            min="0"
                            step="0.01"
                            :disabled="form.customBillingCadence === 'free_forever'"
                            class="mt-1 h-10 w-full rounded-md border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-0 disabled:bg-zinc-100"
                          >
                        </div>
                        <div>
                          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Trial days</label>
                          <input
                            v-model.trim="form.customTrialDays"
                            type="number"
                            min="0"
                            step="1"
                            :disabled="['free_forever', 'one_time'].includes(form.customBillingCadence)"
                            class="mt-1 h-10 w-full rounded-md border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-0 disabled:bg-zinc-100"
                          >
                        </div>
                      </div>
                    </div>

                    <div class="mt-4">
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Payment processor</label>
                      <PlatformaSelect
                        v-model="form.paymentAccountId"
                        class="mt-1"
                        :options="paymentAccountOptions"
                      />
                    </div>

                    <div class="mt-3">
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Payment option</label>
                      <PlatformaSelect
                        v-model="form.paymentOption"
                        class="mt-1"
                        :options="paymentOptionOptions"
                      />
                    </div>

                    <div
                      v-if="form.paymentOption === 'stripe_customer'"
                      class="mt-3"
                    >
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Existing Stripe Customer ID</label>
                      <input
                        v-model.trim="form.stripeCustomerId"
                        type="text"
                        class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                        placeholder="cus_..."
                      >
                    </div>
                  </section>

                  <section
                    v-if="isMemberMode"
                    class="rounded-xl bg-zinc-50 p-4"
                  >
                    <h4 class="text-sm font-semibold uppercase tracking-wide text-zinc-600">
                      Charge Schedule
                    </h4>
                    <div class="mt-3">
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">When should charging begin?</label>
                      <PlatformaSelect
                        v-model="form.chargeMode"
                        class="mt-1"
                        :options="chargeModeOptions"
                      />
                    </div>
                    <div
                      v-if="form.chargeMode === 'scheduled'"
                      class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
                    >
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Date</label>
                        <PlatformaSelect
                          v-model="form.scheduledDate"
                          class="mt-1"
                          :options="dateOptions"
                          menu-placement="top"
                          placeholder="Select date"
                        />
                      </div>
                      <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Time</label>
                        <PlatformaSelect
                          v-model="form.scheduledTime"
                          class="mt-1"
                          :options="timeOptions"
                          menu-placement="top"
                          placeholder="Select time"
                        />
                      </div>
                    </div>
                  </section>

                  <section class="rounded-xl bg-zinc-50 p-4">
                    <h4 class="text-sm font-semibold uppercase tracking-wide text-zinc-600">
                      Creator Actions
                    </h4>
                    <label class="mt-3 flex items-center gap-2 text-sm text-zinc-700">
                      <input
                        v-model="form.sendInviteEmail"
                        type="checkbox"
                        class="h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-0"
                      >
                      Send onboarding email from this action
                    </label>

                    <div
                      v-if="isMemberMode"
                      class="mt-4"
                    >
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Incognito product access
                      </label>
                      <div
                        v-if="!incognitoFeatureOptions.length"
                        class="mt-2 rounded-md bg-white px-3 py-2 text-sm text-zinc-500"
                      >
                        No incognito products found.
                      </div>
                      <div
                        v-else
                        class="app-scroll mt-2 max-h-44 space-y-2 overflow-y-auto rounded-md bg-white p-2"
                      >
                        <label
                          v-for="feature in incognitoFeatureOptions"
                          :key="`manual-member-incognito-${feature.instanceId}`"
                          class="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-zinc-50"
                        >
                          <input
                            v-model="form.incognitoFeatureInstanceIds"
                            type="checkbox"
                            :value="feature.instanceId"
                            class="h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-0"
                          >
                          <span class="text-sm text-zinc-700">{{ feature.label }}</span>
                        </label>
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <div class="border-t border-zinc-200 px-5 py-4 sm:px-6">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <div class="min-h-[1.25rem] flex-1 text-sm text-rose-600">
                    <span v-if="errorMessage">{{ errorMessage }}</span>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      class="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                      :disabled="isSaving"
                      @click="handleClose"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-lg bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2d8bffce] disabled:cursor-not-allowed disabled:opacity-70"
                      :disabled="isSaving || isUploadingAvatar"
                      @click="submit"
                    >
                      <UserPlusIcon class="h-5 w-5" />
                      {{ isSaving ? 'Saving...' : (isMemberMode ? 'Create Member' : 'Create Lead') }}
                    </button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { PhotoIcon, UserPlusIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  mode: { type: String, default: 'member' },
});

const emit = defineEmits(['update:open', 'saved']);
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiBackendUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;
const avatarInputRef = ref(null);
const isSaving = ref(false);
const isUploadingAvatar = ref(false);
const errorMessage = ref('');
const planTiers = ref([]);
const paymentAccountRows = ref([]);
const incognitoFeatureOptions = ref([]);

const form = reactive({
  profilePicUrl: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
  alias: '',
  planMode: 'existing',
  planTierId: '',
  customPlanName: '',
  customBillingCadence: 'monthly',
  customPrice: '0.00',
  customCurrencyCode: 'USD',
  customTrialDays: '0',
  paymentAccountId: '',
  paymentOption: 'none',
  stripeCustomerId: '',
  chargeMode: 'now',
  scheduledDate: '',
  scheduledTime: '',
  sendInviteEmail: true,
  incognitoFeatureInstanceIds: [],
});

const isMemberMode = computed(() => String(props.mode || '').trim().toLowerCase() !== 'lead');
const avatarInitials = computed(() => {
  const first = String(form.firstName || '').trim().charAt(0).toUpperCase();
  const last = String(form.lastName || '').trim().charAt(0).toUpperCase();
  return `${first}${last}`.trim() || 'M';
});

const planModeOptions = Object.freeze([
  { value: 'existing', label: 'Use Existing Tier' },
  { value: 'custom', label: 'Create Custom Tier' },
  { value: 'none', label: 'No Tier Yet' },
]);
const billingCadenceOptions = Object.freeze([
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One-Time Lifetime Access' },
  { value: 'free_forever', label: 'Free Forever' },
]);
const paymentOptionOptions = Object.freeze([
  { value: 'none', label: 'No payment method yet' },
  { value: 'stripe_customer', label: 'Use existing Stripe customer' },
]);
const chargeModeOptions = Object.freeze([
  { value: 'now', label: 'Trigger charge now' },
  { value: 'scheduled', label: 'Start charging on a future date' },
]);

const planTierOptions = computed(() => {
  return (Array.isArray(planTiers.value) ? planTiers.value : []).map((tier) => ({
    value: String(tier.id),
    label: `${String(tier.name || 'Untitled').trim()} - ${tier.priceLabel}`,
  }));
});

const paymentAccountOptions = computed(() => {
  const rows = [{ value: '', label: 'Platforma Payments' }];
  (Array.isArray(paymentAccountRows.value) ? paymentAccountRows.value : []).forEach((account) => {
    if (!account?.id) return;
    rows.push({ value: String(account.id), label: String(account.label || 'Payment Processor') });
  });
  return rows;
});

const dateOptions = computed(() => {
  const now = new Date();
  const rows = [];
  for (let i = 0; i < 90; i += 1) {
    const date = new Date(now);
    date.setDate(now.getDate() + i);
    const value = date.toISOString().slice(0, 10);
    rows.push({
      value,
      label: date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
    });
  }
  return rows;
});

const timeOptions = computed(() => {
  const rows = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const date = new Date();
      date.setHours(h, m, 0, 0);
      rows.push({
        value,
        label: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      });
    }
  }
  return rows;
});

const normalizedRequestBases = computed(() => {
  const values = [apiProxyUrl, apiBackendUrl].map((entry) => String(entry || '').trim()).filter(Boolean);
  return values.length ? Array.from(new Set(values)) : [''];
});

const creatorPaymentsPathCandidates = (path) => {
  const suffix = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return [`/api/creator-payments${suffix}`, `/api/payments${suffix}`];
};

const readJsonResponse = async (response) => response.json().catch(() => ({}));

const requestCreatorPayments = async ({ method = 'GET', path, body = null } = {}) => {
  let lastMessage = '';
  for (const base of normalizedRequestBases.value) {
    for (const candidate of creatorPaymentsPathCandidates(path)) {
      const response = await fetch(`${base}${candidate}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body == null ? undefined : JSON.stringify(body),
      });
      const payload = await readJsonResponse(response);
      if (response.ok && payload?.success !== false) return payload;
      lastMessage = String(payload?.message || payload?.error || `Request failed (${response.status})`);
      if (response.status !== 404) break;
    }
  }
  throw new Error(lastMessage || 'Request failed');
};

const toSlug = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '')
  .slice(0, 190);

const resetForm = () => {
  errorMessage.value = '';
  form.profilePicUrl = '';
  form.firstName = '';
  form.lastName = '';
  form.email = '';
  form.password = '';
  form.phone = '';
  form.alias = '';
  form.planMode = 'existing';
  form.planTierId = '';
  form.customPlanName = '';
  form.customBillingCadence = 'monthly';
  form.customPrice = '0.00';
  form.customCurrencyCode = 'USD';
  form.customTrialDays = '0';
  form.paymentAccountId = '';
  form.paymentOption = 'none';
  form.stripeCustomerId = '';
  form.chargeMode = 'now';
  form.scheduledDate = '';
  form.scheduledTime = '';
  form.sendInviteEmail = true;
  form.incognitoFeatureInstanceIds = [];
};

const loadPlanTiers = async () => {
  try {
    const payload = await requestCreatorPayments({
      method: 'GET',
      path: '/creator/plan-tiers?page=1&pageSize=300&scope=all',
    });
    const rows = Array.isArray(payload?.tiers) ? payload.tiers : [];
    planTiers.value = rows
      .filter((tier) => String(tier?.status || '').trim().toLowerCase() === 'active')
      .filter((tier) => {
        const scope = String(tier?.scope || '').trim().toLowerCase();
        return !scope || scope === 'global';
      })
      .map((tier) => {
        const cadence = String(tier?.billingCadence || 'monthly').trim().toLowerCase();
        const currency = String(tier?.currencyCode || 'USD').trim().toUpperCase() || 'USD';
        const amount = Number(tier?.priceCents || 0) / 100;
        const cadenceLabelMap = {
          daily: '/day',
          weekly: '/wk',
          monthly: '/mo',
          yearly: '/yr',
          one_time: ' lifetime',
          free_forever: '',
        };
        const priceLabel = cadence === 'free_forever'
          ? 'Free'
          : `${currency} ${amount.toFixed(2)}${cadenceLabelMap[cadence] || '/mo'}`;
        return {
          id: Number(tier?.id || 0),
          name: String(tier?.name || '').trim(),
          slug: String(tier?.slug || '').trim(),
          billingCadence: cadence,
          priceLabel,
        };
      })
      .filter((tier) => tier.id > 0);
    if (!form.planTierId && planTiers.value.length) {
      form.planTierId = String(planTiers.value[0].id);
    }
  } catch (_error) {
    planTiers.value = [];
  }
};

const loadPaymentAccounts = async () => {
  try {
    const payload = await requestCreatorPayments({ method: 'GET', path: '/creator/settings' });
    const rows = Array.isArray(payload?.accounts) ? payload.accounts : [];
    paymentAccountRows.value = rows
      .filter((row) => String(row?.status || '').trim().toLowerCase() === 'active')
      .filter((row) => String(row?.provider || '').trim().toLowerCase() !== 'platforma_payments')
      .map((row) => {
        const provider = String(row?.provider || '').trim().toLowerCase();
        let label = String(row?.displayName || '').trim();
        if (!label) {
          if (provider === 'creator_stripe') label = 'Creator Stripe';
          else if (provider === 'bitcoin') label = 'Bitcoin';
          else label = 'Payment Processor';
        }
        return {
          id: Number(row?.id || 0),
          label,
        };
      })
      .filter((row) => row.id > 0);
  } catch (_error) {
    paymentAccountRows.value = [];
  }
};

const loadIncognitoFeatures = async () => {
  if (!isMemberMode.value) {
    incognitoFeatureOptions.value = [];
    return;
  }
  try {
    const response = await fetch(`${apiProxyUrl}/api/incognito-features`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const payload = await readJsonResponse(response);
    if (!response.ok || payload?.success === false) throw new Error('Failed loading incognito features');
    const rows = Array.isArray(payload?.features) ? payload.features : [];
    incognitoFeatureOptions.value = rows
      .map((row) => ({
        instanceId: Number(row?.instanceId || row?.instance_id || 0),
        label: String(row?.customName || row?.custom_name || row?.featureName || row?.feature_name || row?.slug || '').trim() || 'Incognito product',
      }))
      .filter((row) => row.instanceId > 0);
  } catch (_error) {
    incognitoFeatureOptions.value = [];
  }
};

const ensureModalData = async () => {
  await Promise.all([
    loadPlanTiers(),
    loadPaymentAccounts(),
    loadIncognitoFeatures(),
  ]);
};

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    resetForm();
    void ensureModalData();
  }
);

const handleClose = () => {
  if (isSaving.value) return;
  emit('update:open', false);
};

const handleAvatarSelected = async (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;
  isUploadingAvatar.value = true;
  errorMessage.value = '';
  try {
    const sanitizedFilename = encodeURIComponent(String(file.name || 'member-avatar').replace(/[^a-zA-Z0-9._-]/g, '_'));
    const signedResponse = await fetch(`${apiProxyUrl}/api/uploads/generate-signed-url`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: sanitizedFilename,
        contentType: file.type || 'image/png',
        imageType: 'creator_member_manual_avatar',
      }),
    });
    const signedPayload = await readJsonResponse(signedResponse);
    if (!signedResponse.ok || !signedPayload?.signedUrl || !signedPayload?.permanentUrl) {
      throw new Error(String(signedPayload?.message || 'Unable to upload avatar right now.'));
    }

    const uploadResponse = await fetch(String(signedPayload.signedUrl), {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/png' },
      body: file,
    });
    if (!uploadResponse.ok) throw new Error('Failed uploading image.');
    form.profilePicUrl = String(signedPayload.permanentUrl || '').trim();
  } catch (error) {
    errorMessage.value = String(error?.message || 'Avatar upload failed.');
  } finally {
    isUploadingAvatar.value = false;
    if (avatarInputRef.value) avatarInputRef.value.value = '';
  }
};

const buildScheduledIso = () => {
  if (form.chargeMode !== 'scheduled') return new Date().toISOString();
  const datePart = String(form.scheduledDate || '').trim();
  const timePart = String(form.scheduledTime || '').trim();
  if (!datePart || !timePart) return '';
  const isoGuess = `${datePart}T${timePart}:00`;
  const parsed = new Date(isoGuess);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
};

const submit = async () => {
  errorMessage.value = '';
  const firstName = String(form.firstName || '').trim();
  const lastName = String(form.lastName || '').trim();
  const email = String(form.email || '').trim().toLowerCase();
  const password = String(form.password || '');
  if (!firstName || !email) {
    errorMessage.value = 'First name and email are required.';
    return;
  }
  if (isMemberMode.value && (!lastName || !password)) {
    errorMessage.value = 'Last name and password are required for manual member creation.';
    return;
  }
  if (form.planMode === 'existing' && !form.planTierId) {
    errorMessage.value = 'Please select a plan tier or choose another plan mode.';
    return;
  }
  if (form.planMode === 'custom' && !String(form.customPlanName || '').trim()) {
    errorMessage.value = 'Custom tier name is required.';
    return;
  }
  if (form.chargeMode === 'scheduled' && (!form.scheduledDate || !form.scheduledTime)) {
    errorMessage.value = 'Please select both a scheduled date and time.';
    return;
  }

  isSaving.value = true;
  try {
    const selectedTier = planTiers.value.find((tier) => String(tier.id) === String(form.planTierId || '')) || null;
    const nextBillingAtIso = isMemberMode.value ? buildScheduledIso() : '';
    const requestBodyBase = {
      firstName,
      lastName,
      email,
      password: password || '',
      phone: String(form.phone || '').trim(),
      alias: String(form.alias || '').trim(),
      sendInviteEmail: Boolean(form.sendInviteEmail),
      profilePicUrl: String(form.profilePicUrl || '').trim(),
      stripeCustomerId: form.paymentOption === 'stripe_customer' ? String(form.stripeCustomerId || '').trim() : '',
    };

    if (!isMemberMode.value) {
      const fallbackSlug = selectedTier?.slug || toSlug(form.customPlanName || form.planTierId || '');
      const leadResponse = await fetch(`${apiProxyUrl}/api/leads/manual`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...requestBodyBase,
          planTierSlug: fallbackSlug || null,
          source: 'creator_manual_modal',
        }),
      });
      const leadPayload = await readJsonResponse(leadResponse);
      if (!leadResponse.ok || leadPayload?.success === false) {
        throw new Error(String(leadPayload?.message || 'Failed creating lead.'));
      }
      emit('saved', { type: 'lead', lead: leadPayload?.lead || null, wasCreated: Boolean(leadPayload?.wasCreated) });
      emit('update:open', false);
      return;
    }

    const memberResponse = await fetch(`${apiProxyUrl}/api/members/manual`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...requestBodyBase,
        status: 'active',
        subscriptionPlanName: form.planMode === 'custom'
          ? String(form.customPlanName || '').trim()
          : (selectedTier?.name || null),
        incognitoFeatureInstanceIds: Array.isArray(form.incognitoFeatureInstanceIds)
          ? form.incognitoFeatureInstanceIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
          : [],
      }),
    });
    const memberPayload = await readJsonResponse(memberResponse);
    if (!memberResponse.ok || memberPayload?.success === false) {
      throw new Error(String(memberPayload?.message || 'Failed creating member.'));
    }

    const member = memberPayload?.member && typeof memberPayload.member === 'object' ? memberPayload.member : {};
    const memberId = Number(member?.userId || member?.user_id || 0);
    if (memberId > 0) {
      const metadata = {
        source: 'creator_manual_modal',
        nextBillingAt: nextBillingAtIso || new Date().toISOString(),
      };
      if (form.planMode === 'custom') {
        const cadence = String(form.customBillingCadence || 'monthly').trim().toLowerCase() || 'monthly';
        const numericPrice = Number(form.customPrice || 0);
        const safePriceCents = cadence === 'free_forever' ? 0 : Math.max(0, Math.round((Number.isFinite(numericPrice) ? numericPrice : 0) * 100));
        const trialDays = ['free_forever', 'one_time'].includes(cadence)
          ? 0
          : Math.max(0, Math.trunc(Number(form.customTrialDays || 0) || 0));
        await requestCreatorPayments({
          method: 'POST',
          path: `/creator/members/${encodeURIComponent(String(memberId))}/plan-subscription/custom`,
          body: {
            name: String(form.customPlanName || '').trim(),
            slug: toSlug(form.customPlanName || ''),
            billingCadence: cadence,
            priceCents: safePriceCents,
            currencyCode: String(form.customCurrencyCode || 'USD').trim().toUpperCase() || 'USD',
            trialDays,
            status: 'active',
            description: '',
            paymentAccountId: form.paymentAccountId ? Number(form.paymentAccountId) : null,
            assignToMember: true,
            metadata,
          },
        });
      } else if (form.planMode === 'existing' && selectedTier?.id) {
        await requestCreatorPayments({
          method: 'PUT',
          path: `/creator/members/${encodeURIComponent(String(memberId))}/plan-subscription`,
          body: {
            planTierId: Number(selectedTier.id),
            status: 'active',
            metadata,
          },
        });
      }
    }

    emit('saved', { type: 'member', member, memberId });
    emit('update:open', false);
  } catch (error) {
    errorMessage.value = String(error?.message || 'Unable to save this record.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(113, 113, 122, 0.4) transparent;
}

.app-scroll::-webkit-scrollbar {
  width: 7px;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(113, 113, 122, 0.35);
}

.app-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(82, 82, 91, 0.45);
}
</style>
